// 案件ブックマーク（No.429）のローカルストレージアクセスを一元化するユーティリティ。
// 将来サーバー保存へ切り替える場合は、この内部実装をGraphQL呼び出しに置き換えるだけで済むよう、
// 呼び出し側は本モジュールが公開する関数のシグネチャ（Promiseベース）のみに依存すること。
import { BookmarkKey } from "../contexts/types";
import { BookmarkMaxCount } from "../contexts/constants";

const BOOKMARK_STORAGE_KEY = "ceas_bookmarks";

/**
 * ローカルストレージからブックマーク一覧を取得する
 * @returns ブックマークキー配列（未保存時は空配列）
 */
export const getBookmarks = async (): Promise<BookmarkKey[]> => {
  try {
    const stored = localStorage.getItem(BOOKMARK_STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch (error) {
    console.error("getBookmarks ERROR:", error);
    return [];
  }
};

/**
 * projectId・reviewDateが一致するブックマークが登録済みかどうかを判定する
 * @param projectId プロジェクトID
 * @param reviewDate 振り返り日（通常モードはnull）
 * @returns 登録済みならtrue
 */
export const isBookmarked = async (projectId: string, reviewDate: string | null): Promise<boolean> => {
  const bookmarks = await getBookmarks();
  return bookmarks.some(b => b.projectId === projectId && (b.reviewDate || null) === (reviewDate || null));
};

/**
 * ブックマークを追加する（上限到達時は追加せずfalseを返す）
 * @param projectId プロジェクトID
 * @param reviewDate 振り返り日（通常モードはnull）
 * @returns 追加できた場合true、上限到達や重複で追加しなかった場合false
 */
export const addBookmark = async (projectId: string, reviewDate: string | null): Promise<boolean> => {
  try {
    const bookmarks = await getBookmarks();
    const normalizedReviewDate = reviewDate || null;
    const alreadyExists = bookmarks.some(b => b.projectId === projectId && (b.reviewDate || null) === normalizedReviewDate);
    if (alreadyExists) return false;
    if (bookmarks.length >= BookmarkMaxCount) return false;

    bookmarks.push({ projectId, reviewDate: normalizedReviewDate });
    localStorage.setItem(BOOKMARK_STORAGE_KEY, JSON.stringify(bookmarks));
    return true;
  } catch (error) {
    console.error("addBookmark ERROR:", error);
    return false;
  }
};

/**
 * ブックマークを解除する
 * @param projectId プロジェクトID
 * @param reviewDate 振り返り日（通常モードはnull）
 */
export const removeBookmark = async (projectId: string, reviewDate: string | null): Promise<void> => {
  try {
    const bookmarks = await getBookmarks();
    const normalizedReviewDate = reviewDate || null;
    const filtered = bookmarks.filter(b => !(b.projectId === projectId && (b.reviewDate || null) === normalizedReviewDate));
    localStorage.setItem(BOOKMARK_STORAGE_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error("removeBookmark ERROR:", error);
  }
};
